import { SafeUrl } from '@angular/platform-browser';
import { MetricUpdate, TimestreamMetric, TimestreamMetricOutput } from '@app/home/models/metric.model';
import { DateTime } from 'luxon';
import { PathGraphResponse } from '../../gas-block/models/path-request.model';

export interface Dashboard {
  id: string;
  name: string;
  creatorName: string;
  creatorEmail: string;
  creationDate: string;
  editionDate: string;
  privacy: DashboardPrivacy;
  width: number;
  height: number;
  components: Array<DashboardComponent>;
  isBlocksLocked: boolean;
  currentlyDraftBy: string;
  // front only
  loaded?: boolean;
}

export interface DashboardLimitedData {
  id: string;
  name: string;
}

export interface DashboardComponent {
  id: number;
  type: DashboardComponentType;
  x: number;
  y: number;
  width: number;
  height: number;
  text?: DashboardText;
  image?: DashboardImage;
  link?: DashboardLink;
  curve?: DashboardCurve;
  path?: DashboardPath;
  shape?: DashboardShape;
  digitalMeter?: DashboardDigitalMeter;
  analysis?: DashboardAnalysis;
  // gridster only
  cols?: number;
  rows?: number;
}

export enum DashboardComponentType {
  TEXT,
  BUTTON,
  IMAGE,
  CURVE,
  PATH,
  SHAPE,
  LINK,
  DIGITALMETER,
  ANALYSIS
}

export enum DashboardPrivacy {
  PRIVATE,
  PUBLIC,
  REQUEST
}

export interface DashboardText {
  text: string;
  size: number;
  style: string;
  align: string;
  textFont: string;
  textColor: string;
  responsiveSize: boolean;
  backgroundColor: string;
}

export interface DashboardImage {
  id: string;
  url?: SafeUrl;
  name?: string;
  display: DashboardImageDisplay;
  align: DashboardImageAlign;
  crop: string;
  backgroundColor: string;
}

export interface DashboardImageToUpload {
  image: File;
  componentId: number;
}

export interface DashboardLink extends DashboardText {
  linkType: LinkEnumType;
  borderSize: number;
  borderColor: string;
  externalLink: string;
  linkedToDashboardId: string;
  linkedDashboardName: string;
}

export interface DashboardCurve {
  title: string;
  steps: number;
  range: number;
  legend: boolean;
  hint: boolean;
  isGraph: boolean;
  metrics: Array<TimestreamMetric>;
  backgroundColor: string;
  // button
  button: boolean;
  text: string;
  size: number;
  style: string;
  align: string;
  textFont: string;
  textColor: string;
  responsiveSize: boolean;
  // dynamic
  nbColumns: number;
  data: Array<TimestreamMetricOutput>;
  chart: { start: DateTime; end: DateTime; metrics: any };
}

export interface DashboardPath {
  path: string;
  points: Array<string>;
  equipments: string;
  title: boolean;
  legend: boolean;
  gasBlock: boolean;
  data1: string;
  data2: string;
  color1: string;
  color2: string;
  primaryColor: string;
  backgroundColor: string;
  // button
  button: boolean;
  text: string;
  size: number;
  style: string;
  align: string;
  textFont: string;
  textColor: string;
  responsiveSize: boolean;
  // dynamic
  chart: { start: DateTime; end: DateTime; metrics: any };
  data?: PathGraphResponse;
}

export interface DashboardShape {
  shape: string;
  orientation: number;
  colors: number;
  primaryColor: string;
  secondaryColor: string;
  text: string;
  size: number;
  stroke: number;
  static: boolean;
  backgroundColor: string;
}

export interface DashboardDigitalMeter {
  name: string;
  borderSize: number;
  nbDecimal: number;
  backgroundColor: string;
  tag: string;
  isKiara: boolean;
  unit: string;
  physicalQuantity: string;
  displayUnit: string;
  convertAbsolute: boolean;
  label: boolean;
  value: number;
}

export interface DashboardAnalysis {
  text: string;
  size: number;
  style: string;
  align: string;
  textFont: string;
  textColor: string;
  responsiveSize: boolean;
  backgroundColor: string;
  properties: Array<string>;
  isGraph: boolean;
  steps: string;
  range: string;
}

export interface IndabaInstantResponse {
  isResponseComplete: boolean;
  name: string;
  unit: string;
  values: MetricUpdate[];
}

export enum DashboardImageDisplay {
  INSERTED,
  STRETCHED,
  MAXIMIZED
}

export enum DashboardImageAlign {
  CENTER,
  TOPLEFT,
  TOP,
  TOPRIGHT,
  LEFT,
  RIGHT,
  BOTTOMLEFT,
  BOTTOM,
  BOTTOMRIGHT
}

export interface DashboardOrderRaw {
  email: string;
  dashboards: {
    [key: string]: string;
  };
}

export interface DashboardOrder {
  email: string;
  dashboards: Array<string>;
}

// good way for enum
export const LinkConst = {
  DASHBOARD: 'DASHBOARD',
  EXTERNAL: 'EXTERNAL',
  TOPOLOGIE: 'TOPOLOGIE'
} as const;
export type LinkEnumType = (typeof LinkConst)[keyof typeof LinkConst];
